import { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent
} from "../components/ui/dialog";
import {
  Search,
  TreeDeciduous,
  X,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Camera
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const isAgronomist = user?.role === "agronomist";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTrees();
  }, [user]);

  const fetchTrees = async () => {
    try {
      const endpoint = isAgronomist ? "/trees/all" : "/trees";
      const response = await axios.get(`${API}${endpoint}`, {
        withCredentials: true
      });
      setTrees(response.data.filter((t) => t.photo_url));
    } catch (error) {
      console.error("Error fetching trees:", error);
      toast.error("Erro ao carregar galeria");
    } finally {
      setLoading(false);
    }
  };

  const filteredTrees = trees.filter(
    (tree) =>
      tree.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tree.species.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleImageClick = (tree, index) => {
    setSelectedImage(tree);
    setCurrentIndex(index);
  };

  const handlePrev = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : filteredTrees.length - 1;
    setSelectedImage(filteredTrees[newIndex]);
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < filteredTrees.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(filteredTrees[newIndex]);
    setCurrentIndex(newIndex);
  };

  const handleKeyDown = (e) => {
    if (!selectedImage) return;
    if (e.key === "ArrowLeft") handlePrev();
    if (e.key === "ArrowRight") handleNext();
    if (e.key === "Escape") setSelectedImage(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, currentIndex]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="animate-fade-in">
            <h1 className="text-2xl md:text-3xl font-bold font-['Outfit']">
              Galeria de Fotos
            </h1>
            <p className="text-muted-foreground">
              {filteredTrees.length} {filteredTrees.length === 1 ? "foto" : "fotos"}{" "}
              {searchQuery && "encontradas"}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 animate-fade-in stagger-1">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="gallery-search"
              type="text"
              placeholder="Buscar por nome ou espécie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 rounded-full bg-accent/50 border-transparent focus:bg-background"
            />
          </div>
        </div>

        {/* Gallery Grid */}
        {filteredTrees.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredTrees.map((tree, index) => (
              <GalleryItem
                key={tree.tree_id}
                tree={tree}
                index={index}
                onClick={() => handleImageClick(tree, index)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 animate-fade-in">
            <Camera className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {searchQuery ? "Nenhuma foto encontrada" : "Nenhuma foto na galeria"}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery
                ? "Tente buscar com outros termos"
                : "Adicione fotos às suas árvores para visualizá-las aqui"}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate("/trees")} className="btn-primary">
                <TreeDeciduous className="w-4 h-4 mr-2" />
                Ver Árvores
              </Button>
            )}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
            <div className="relative">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-6 h-6" />
              </Button>

              {/* Navigation */}
              {filteredTrees.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                    onClick={handlePrev}
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                    onClick={handleNext}
                  >
                    <ChevronRight className="w-8 h-8" />
                  </Button>
                </>
              )}

              {/* Image */}
              <div className="flex items-center justify-center min-h-[60vh] p-8">
                {selectedImage?.photo_url && (
                  <img
                    src={selectedImage.photo_url}
                    alt={selectedImage.name}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  />
                )}
              </div>

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white text-xl font-semibold mb-1">
                  {selectedImage?.name}
                </h3>
                <p className="text-white/70">{selectedImage?.species}</p>
                <p className="text-white/50 text-sm mt-2">
                  {currentIndex + 1} / {filteredTrees.length}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const GalleryItem = ({ tree, index, onClick }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      data-testid={`gallery-item-${tree.tree_id}`}
      className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-accent card-shadow animate-fade-in"
      style={{ animationDelay: `${index * 0.03}s` }}
      onClick={onClick}
    >
      {tree.photo_url && !imageError ? (
        <img
          src={tree.photo_url}
          alt={tree.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageOff className="w-8 h-8 text-muted-foreground/30" />
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
        <p className="text-white font-medium text-sm truncate">{tree.name}</p>
        <p className="text-white/70 text-xs truncate">{tree.species}</p>
      </div>
    </div>
  );
};

export default GalleryPage;
